import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Handshake, MagnifyingGlass, CaretRight } from "@phosphor-icons/react";
import SalesAnalytics from "@/components/sales/SalesAnalytics";

export const SALES_STATUSES = ["Lead", "In Progress", "Finalized"];
export const SALES_STATUS_COLORS = {
  Lead: "bg-slate-100 text-slate-700",
  "In Progress": "bg-amber-50 text-amber-700",
  Finalized: "bg-emerald-50 text-emerald-700",
};

const TABS = [
  { key: "current", label: "Current" },
  { key: "finalized", label: "Finalized" },
  { key: "analytics", label: "Analytics" },
];

export default function Sales() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("current");
  const [data, setData] = useState({ clients: [], counts: { current: 0, won: 0, lost: 0 }, statuses: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [assignees, setAssignees] = useState([]);

  const fetchClients = useCallback(async (t, q, silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/sales/clients", { params: { tab: t, search: q } });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load sales clients");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    api.get("/sales/assignees").then((r) => setAssignees(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (tab === "analytics") { setLoading(false); return; }
    const id = setTimeout(() => fetchClients(tab, search), 250);
    return () => clearTimeout(id);
  }, [tab, search, fetchClients]);

  const openClient = (email) => navigate(`/sales/client?email=${encodeURIComponent(email)}`);

  // Inline update from the list — stops the row click, PATCHes, then refreshes
  // the current tab (a status change may move the client to another tab).
  const inlinePatch = async (email, body, okMsg) => {
    try {
      await api.patch("/sales/clients/detail", body, { params: { email } });
      if (okMsg) toast.success(okMsg);
      fetchClients(tab, search, true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
      data-testid="sales-page"
    >
      {/* Header */}
      <div className="flex items-start gap-4 flex-wrap">
        <div className="h-12 w-12 rounded-sm bg-slate-900/5 flex items-center justify-center shrink-0">
          <Handshake size={24} className="text-slate-900" />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="font-display text-3xl text-slate-900">Sales</h1>
          <p className="text-sm text-slate-500 mt-1">
            Every client who has ever enquired, grouped by their email address. Working a client here is standalone — it never changes the underlying leads.
          </p>
        </div>
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="sales-search"
            placeholder="Search client, email, phone…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 rounded-sm pl-8 w-[260px] text-sm"
          />
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="sales-tabs">
          {TABS.map((t) => (
            <TabsTrigger key={t.key} value={t.key} data-testid={`sales-tab-${t.key}`}>
              {t.label}
              {t.key !== "analytics" && <span className="ml-1.5 text-xs opacity-60">({data.counts?.[t.key] ?? 0})</span>}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {tab === "analytics" ? (
        <SalesAnalytics onOpenClient={openClient} />
      ) : loading ? (
        <p className="text-sm text-slate-500" data-testid="sales-loading">Loading…</p>
      ) : data.clients.length === 0 ? (
        <div className="border border-dashed border-slate-200 rounded-sm p-12 text-center" data-testid="sales-empty">
          <Handshake size={36} className="mx-auto text-slate-300" />
          <p className="mt-3 text-sm text-slate-500">No clients in this tab.</p>
        </div>
      ) : (
        <div className="border border-slate-200 rounded-sm overflow-x-auto" data-testid="sales-list">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-slate-500">
                <th className="py-3 px-4 label-tracking !text-[0.65rem]">Client Name</th>
                <th className="py-3 px-4 label-tracking !text-[0.65rem]">Assigned User</th>
                <th className="py-3 px-4 label-tracking !text-[0.65rem]">Status</th>
                <th className="py-3 px-4 label-tracking !text-[0.65rem] text-right">Leads</th>
                <th className="py-3 px-4 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {data.clients.map((c) => (
                <tr
                  key={c.email}
                  data-testid={`sales-row-${c.email}`}
                  onClick={() => openClient(c.email)}
                  className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                >
                  <td className="py-3 px-4">
                    <div className="font-medium text-slate-900">{c.name}</div>
                    <div className="text-xs text-slate-400 font-mono">{c.email}</div>
                  </td>
                  <td className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
                    <Select
                      value={c.assigned_to || "__none__"}
                      onValueChange={(v) => inlinePatch(c.email, { assigned_to: v === "__none__" ? "" : v }, "Assignment updated")}
                    >
                      <SelectTrigger data-testid={`sales-assign-${c.email}`} className="h-8 rounded-sm text-xs w-[180px] border-slate-200">
                        <SelectValue placeholder="Unassigned" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Unassigned</SelectItem>
                        {assignees.map((a) => (
                          <SelectItem key={a.email} value={a.email}>{a.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
                    <Select
                      value={c.status}
                      onValueChange={(v) => inlinePatch(c.email, { status: v }, `Moved to ${v}`)}
                    >
                      <SelectTrigger data-testid={`sales-status-${c.email}`} className="h-8 rounded-sm text-xs w-[150px] border-slate-200">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {["Lead", "In Progress", "Finalized"].map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="py-3 px-4 text-right text-slate-500 font-mono text-xs">{c.lead_count}</td>
                  <td className="py-3 px-4 text-slate-300"><CaretRight size={14} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </motion.div>
  );
}
